import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { AiOutlineCamera } from 'react-icons/ai';
import ZoomableImage from './ZoomableImage';
import { apiUrl } from '../config/api';
import './PublicTimelinePage.css';

const formatDateDisplay = (dateString) => {
  if (!dateString || dateString === 'Undated') return 'Undated';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString('en-US', {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
    });
  } catch {
    return dateString;
  }
};

// Same grouping convention used in CompanyCamPage's own client-detail grid, so the public
// timeline reads identically: most recent date first, undated photos last.
const groupImagesByDate = (images) => {
  const grouped = {};
  images.forEach((image) => {
    const date = image.inspectionDate || 'Undated';
    if (!grouped[date]) grouped[date] = [];
    grouped[date].push(image);
  });
  return Object.entries(grouped).sort((a, b) => {
    if (a[0] === 'Undated') return 1;
    if (b[0] === 'Undated') return -1;
    return new Date(b[0]) - new Date(a[0]);
  });
};

// No auth — reachable by anyone holding the link. Read-only besides the upload button: name,
// address, a hero "main photo," and the full-quality timeline grouped by date. Uploading here
// adds photos to this same client (the link can't create a new one).
const PublicTimelinePage = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadMessage, setUploadMessage] = useState('');
  const [previewIndex, setPreviewIndex] = useState(null);
  const fileInputRef = useRef(null);

  const closePreview = () => setPreviewIndex(null);
  const previewNext = () => {
    setPreviewIndex((i) => (i === null ? i : (i + 1) % data.images.length));
  };
  const previewPrev = () => {
    setPreviewIndex((i) => (i === null ? i : (i - 1 + data.images.length) % data.images.length));
  };

  useEffect(() => {
    if (previewIndex === null) return undefined;
    const handleKey = (e) => {
      if (e.key === 'ArrowRight') previewNext();
      else if (e.key === 'ArrowLeft') previewPrev();
      else if (e.key === 'Escape') closePreview();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewIndex, data]);

  const loadTimeline = useCallback(async () => {
    try {
      const response = await fetch(apiUrl(`/api/public/timeline/${token}`));
      const body = await response.json();
      if (!response.ok) {
        setError(body.message || 'This timeline link is invalid or has expired');
        return;
      }
      setData(body);
    } catch (err) {
      setError('Failed to load timeline');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadTimeline();
  }, [loadTimeline]);

  const handleFilesSelected = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = ''; // let picking the exact same file(s) again re-trigger onChange
    if (files.length === 0) return;

    setUploading(true);
    setUploadMessage('');

    const formData = new FormData();
    files.forEach((file) => formData.append('images', file));

    try {
      const response = await fetch(apiUrl(`/api/public/timeline/${token}/upload`), {
        method: 'POST',
        body: formData,
      });
      const body = await response.json();
      if (!response.ok) {
        setUploadMessage(body.message || 'Upload failed');
        return;
      }
      setUploadMessage(body.message);
      await loadTimeline();
    } catch (err) {
      setUploadMessage('Upload failed — check your connection and try again');
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return <div className="pt-state">Loading timeline&hellip;</div>;
  }

  if (error) {
    return <div className="pt-state pt-state--error">{error}</div>;
  }

  return (
    <div className="pt-page">
      <div className="pt-hero">
        <div className="pt-hero-info">
          <img src="/logo-mark.png" alt="Capturify" className="pt-logo" />
          <p className="pt-address">{data.clientAddress}</p>
          <h1 className="pt-title">{data.clientName}</h1>
        </div>
        {data.coverImageUrl && (
          <div className="pt-hero-photo">
            <img src={data.coverImageUrl} alt={data.clientName} />
          </div>
        )}
      </div>

      <div className="pt-timeline">
        {uploadMessage && <div className="pt-upload-msg">{uploadMessage}</div>}

        {data.images.length === 0 ? (
          <div className="pt-state">No photos yet.</div>
        ) : (
          groupImagesByDate(data.images).map(([date, images]) => (
            <div key={date} className="pt-date-group">
              <div className="pt-date-header">{formatDateDisplay(date)}</div>
              <div className="pt-grid">
                {images.map((img) => (
                  <div
                    key={img.id}
                    className="pt-photo"
                    onClick={() => setPreviewIndex(data.images.findIndex((i) => i.id === img.id))}
                  >
                    {img.mediaType === 'video' && !img.imageUrl ? (
                      <div className="pt-video-placeholder" aria-label="Video" />
                    ) : (
                      <img
                        src={img.imageUrl}
                        alt={data.clientName}
                        loading="lazy"
                        style={img.rotation ? { transform: `rotate(${img.rotation}deg)` } : undefined}
                      />
                    )}
                    {img.mediaType === 'video' && <span className="pt-video-play" aria-hidden="true" />}
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>

      {previewIndex !== null && (() => {
        const image = data.images[previewIndex];
        if (!image) return null;
        return (
          <div className="pt-preview-overlay" onClick={closePreview}>
            {data.images.length > 1 && (
              <button
                className="pt-preview-nav pt-preview-nav--prev"
                onClick={(e) => { e.stopPropagation(); previewPrev(); }}
                aria-label="Previous photo"
              >
                &#8249;
              </button>
            )}
            <div className="pt-preview-modal" onClick={(e) => e.stopPropagation()}>
              <button className="pt-preview-close" onClick={closePreview} aria-label="Close">&#x2715;</button>
              {/* resetKey on the index so switching photos starts fresh each time. */}
              {image.mediaType === 'video' ? (
                <div className="pt-preview-video-stage" onClick={closePreview}>
                  <video
                    key={image.id}
                    className="pt-preview-video"
                    src={image.videoUrl}
                    poster={image.imageUrl || undefined}
                    controls
                    playsInline
                    autoPlay
                    preload="metadata"
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              ) : (
                <ZoomableImage
                  resetKey={previewIndex}
                  src={image.imageUrl}
                  alt={data.clientName}
                  rotation={image.rotation || 0}
                  onBackdropClick={closePreview}
                />
              )}
              <div className="pt-preview-counter">{previewIndex + 1} / {data.images.length}</div>
            </div>
            {data.images.length > 1 && (
              <button
                className="pt-preview-nav pt-preview-nav--next"
                onClick={(e) => { e.stopPropagation(); previewNext(); }}
                aria-label="Next photo"
              >
                &#8250;
              </button>
            )}
          </div>
        );
      })()}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        capture="environment"
        onChange={handleFilesSelected}
        style={{ display: 'none' }}
      />
      <button
        className="pt-upload-fab"
        onClick={() => fileInputRef.current?.click()}
        disabled={uploading}
        title="Upload photos"
        aria-label="Upload photos"
      >
        <AiOutlineCamera size={22} />
        {uploading && <span className="pt-upload-fab-spinner" />}
      </button>
    </div>
  );
};

export default PublicTimelinePage;
